import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import type { MouseEvent } from "react";
import { Link as RouterLink } from "react-router";

const Breadcrumb = styled("span")(({ theme }) => ({
  color: "gray",
  display: "inline-flex",
  marginRight: theme.spacing(1),
  whiteSpace: "nowrap",
}));

// The breadcrumb keeps the title's grey and weight, so it reads as the trail it is. A dotted
// underline at rest is what says it can be followed without shouting in a heading; it turns solid
// on hover and on keyboard focus, so the pointer and the keyboard both see it answer. The underline
// takes the text's colour: Material tints a link's underline with the primary colour, which on this
// grey reads as a stray blue line rather than part of the word.
const BreadcrumbLink = styled(Link)(() => ({
  color: "inherit",
  textDecorationColor: "currentColor",
  textDecorationLine: "underline",
  textDecorationStyle: "dotted",
  textDecorationThickness: "from-font",
  textUnderlineOffset: "0.15em",
  "&:hover, &:focus-visible": {
    textDecorationStyle: "solid",
  },
})) as typeof Link;

/**
 * The name for a heading that opens with breadcrumbs: the trail as it reads, "Permissions > Kinds > Signing
 * authority".
 *
 * A heading takes its name from its content, and each crumb in it is named "Back to …", so without this
 * a screen reader's list of headings reads "Back to Permissions > Back to Kinds > Signing authority"
 * (#6324). Give it to the heading as its `aria-label`; the crumbs keep their own names as links.
 */
export function breadcrumbHeadingName(trail: readonly string[], title: string) {
  return [...trail, title].join(" > ");
}

/**
 * The "Section >" that opens a page title, as the way back to that section's list. The heading it opens
 * takes its name from `breadcrumbHeadingName`.
 *
 * A page that once drew a back arrow or a "Back" chip beside its breadcrumb said the same thing twice,
 * and on a phone it spent the start of the narrowest row on it (#5907). The proposal pages had already
 * made the crumb itself the control; this is the same crumb, so every page that does it looks, reads
 * and is announced alike — "Back to Proposals", "Back to Work Agreements".
 *
 * It is a real link, so it takes focus, follows on Enter, and opens in a new tab on a modified click.
 * `onFollow` is for a page that has to ask something first — whether to discard unsaved changes, say:
 * a plain click or Enter then goes to `onFollow` instead of the router, and the page navigates itself.
 * A modified click is left to the browser, because a new tab leaves this page's changes where they are.
 */
export default function PageTitleBreadcrumb(props: Readonly<{
  label: string;
  to: string;
  replace?: boolean;
  onFollow?: () => void;
}>) {
  const { label, onFollow, replace, to } = props;

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!onFollow) return;
    if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    onFollow();
  }

  // Named here as well as by the tooltip, which names its child only while it is the real Tooltip.
  // The visible word stays inside the name, so a speech user saying "Teams" still reaches it.
  const name = `Back to ${label}`;
  return (
    <Breadcrumb>
      <Tooltip title={name} placement="bottom">
        <BreadcrumbLink aria-label={name} component={RouterLink} to={to} replace={replace} onClick={handleClick}>
          {label}
        </BreadcrumbLink>
      </Tooltip>
      &nbsp;&gt;
    </Breadcrumb>
  );
}
